import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import NavBar from "../NavBar/NavBar";
import ApplicationCard from "../../common/ApplicationCard";
import { useApplications } from "../../../hooks/useApplications";
import { useJWTToken } from "../../../hooks/useJWTToken";
import NavigationConstants from "../../../enums/NavigationConstants";
import FooterData from "../NavBar/FooterData";
import './Home.css';
import {
  checkUserAuthentication,
  handleUnauthenticatedUser,
  isLogoutInProgress,
  isAuthInProgress,
  searchAndStoreCurrentUser,
  getCurrentUserData
} from "../../../utils/authUtils";

const STATIC_APPLICATIONS = [
    {
        title: "New Access Request",
        description: "Click here to request for a new Application",
        url: "/request-dashboard"
    },
    {
        title: "External Request History",
        description: "View your submitted role requests and their status",
        url: "/external-request-history"
    }
];

const ExternalHome: React.FC = () => {
    const navigate = useNavigate();
    const { parsedToken, isProcessing, error: tokenError } = useJWTToken();
    const { applications, loading, error } = useApplications();
    const [sessionUserData, setSessionUserData] = useState<any>(null);
    const [isAuthenticating, setIsAuthenticating] = useState(true);
    const [authError, setAuthError] = useState<string | null>(null);

    // Helper function to construct B2C authorization URL with application redirect
    const constructB2CAuthUrl = (applicationUrl: string | null): string => {
        if (!applicationUrl) {
            return '#'; // Return placeholder if URL is null
        }
        const encodedRedirectUri = encodeURIComponent(applicationUrl);
        return `https://stateofneb2cdev.b2clogin.com/stateofneb2cdev.onmicrosoft.com/oauth2/v2.0/authorize?p=B2C_1A_IDENTITY_MANAGEMENT_SIGNUP_SIGNIN&client_id=adeb9e71-23dc-433a-b84f-6dcc963aa6cb&nonce=defaultNonce&redirect_uri=${encodedRedirectUri}&scope=openid adeb9e71-23dc-433a-b84f-6dcc963aa6cb&response_type=code&isMFA=true&state=b2c&response_mode=query`;
    };

    useEffect(() => {
        const initializeAuth = async () => {
            // If logout is in progress, redirect to login
            if (isLogoutInProgress()) {
                console.log("Logout in progress, redirecting to login...");
                navigate("/");
                return;
            }

            // Check for authentication errors
            if (tokenError) {
                console.error("Token processing error:", tokenError);
                setAuthError(tokenError);
                handleUnauthenticatedUser();
                return;
            }

            // If token processing is in progress, wait briefly
            if (isProcessing) {
                console.log("Token processing in progress, waiting...");
                return;
            }

            // Check if user is authenticated
            if (!checkUserAuthentication()) {
                // If auth is in progress, wait a bit longer
                if (isAuthInProgress()) {
                    console.log("Authentication in progress, waiting...");
                    return;
                }
                console.log("User not authenticated, redirecting to login");
                handleUnauthenticatedUser();
                return;
            }

            // User is authenticated, proceed immediately
            setIsAuthenticating(false);

            // Always call search-and-update on login to sync user data and set status to Active
            // This runs asynchronously in the background (doesn't block the UI)
            searchAndStoreCurrentUser().then(userData => {
                setSessionUserData(userData);
                console.log("User data synced and status set to Active");
            }).catch(error => {
                console.error("Failed to sync user data:", error);
                // Fall back to cached data if available
                const cachedUserData = getCurrentUserData();
                if (cachedUserData) {
                    setSessionUserData(cachedUserData);
                }
            });
        };

        initializeAuth();
    }, [navigate, isProcessing, tokenError, parsedToken]);

    // Refresh user data when needed
    const refreshUserData = async () => {
        const userData = await searchAndStoreCurrentUser();
        setSessionUserData(userData);
    };

    // Show loading while authenticating or loading apps
    if (loading || isAuthenticating || isProcessing) {
        return (
            <>
                <NavBar />
                <Box sx={{
                    padding: 4,
                    minHeight: 'calc(100vh - 64px)',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Typography variant="h6" sx={{ color: '#333' }}>
                        {isProcessing ? 'Completing sign-in...' :
                         isAuthenticating ? 'Setting up your account...' :
                         'Loading your applications...'}
                    </Typography>
                    {(tokenError || authError) && (
                        <Typography variant="body2" sx={{ color: 'red', marginTop: 2 }}>
                            {tokenError || authError}
                        </Typography>
                    )}
                </Box>
                <FooterData styles={true} />
            </>
        );
    }

    return (
        <>
            <NavBar />
            <Box sx={{
                padding: 4,
                minHeight: 'calc(100vh - 64px)',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>
                {/* My Applications Section */}
                {applications.length > 0 && (
                    <Box sx={{
                        width: '100%',
                        maxWidth: '900px', // Limit max width for better centering
                        marginBottom: 6
                    }}>
                        <Typography
                            variant="h4"
                            sx={{
                                textAlign: 'center',
                                marginBottom: 4,
                                fontWeight: 'bold',
                                color: '#333'
                            }}
                        >
                            My Applications
                        </Typography>

                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)', // Exactly 3 columns
                            gap: 3,
                            justifyItems: 'center',
                            // Responsive: reduce to 2 columns on medium screens, 1 on small
                            '@media (max-width: 900px)': {
                                gridTemplateColumns: 'repeat(2, 1fr)',
                            },
                            '@media (max-width: 600px)': {
                                gridTemplateColumns: '1fr',
                            }
                        }}>
                            {applications.map((app, index) => (
                                <Box
                                    key={`dynamic-${index}`}
                                    sx={{
                                        width: 280,
                                        height: 200,
                                        minWidth: 280,
                                        minHeight: 200,
                                        maxWidth: 280,
                                        maxHeight: 200,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <ApplicationCard
                                        title={app.applicationName}
                                        description={app.applicationDescription}
                                        url={constructB2CAuthUrl(app.applicationUrl)}
                                        isExternal={true}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Request Management Section */}
                {STATIC_APPLICATIONS.length > 0 && (
                    <Box sx={{
                        width: '100%',
                        maxWidth: '900px', // Limit max width for better centering
                        marginBottom: 6
                    }}>
                        <Typography
                            variant="h4"
                            sx={{
                                textAlign: 'center',
                                marginBottom: 4,
                                fontWeight: 'bold',
                                color: '#333'
                            }}
                        >
                            Request Management
                        </Typography>

                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)', // Exactly 3 columns
                            gap: 3,
                            justifyItems: 'center',
                            // Responsive: reduce to 2 columns on medium screens, 1 on small
                            '@media (max-width: 900px)': {
                                gridTemplateColumns: 'repeat(2, 1fr)',
                            },
                            '@media (max-width: 600px)': {
                                gridTemplateColumns: '1fr',
                            }
                        }}>
                            {STATIC_APPLICATIONS.map((app, index) => (
                                <Box
                                    key={`static-${index}`}
                                    sx={{
                                        width: 280,
                                        height: 200,
                                        minWidth: 280,
                                        minHeight: 200,
                                        maxWidth: 280,
                                        maxHeight: 200,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <ApplicationCard
                                        title={app.title}
                                        description={app.description}
                                        url={app.url}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Error Display */}
                {error && (
                    <Box sx={{
                        width: '100%',
                        maxWidth: '900px',
                        textAlign: 'center',
                        marginTop: 4
                    }}>
                        <Typography variant="h6" sx={{ color: 'red', padding: '20px' }}>
                            Error loading applications: {error}
                        </Typography>
                    </Box>
                )}
            </Box>
            <FooterData styles={true} />
        </>
    );
};

export default ExternalHome;